/**
 * Supabase Cloud Sync Service
 * Syncs auto-save entries between IndexedDB and Supabase cloud storage
 */

import { supabase, isSupabaseConfigured } from '@/config/supabase';
import {
  saveAutoSave,
  getAutoSaves,
  saveProject,
  getProjects,
  saveDesign,
  getDesigns,
  getCartItems,
  saveCartItem,
  type AutoSaveEntry,
  type StoredProject,
  type StoredDesign,
  type CartItem,
} from './storage';

/**
 * Upload auto-save entry to Supabase cloud storage
 */
export async function uploadAutoSaveToCloud(entry: AutoSaveEntry): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  const { error } = await supabase.from('auto_save_history').insert({
    id: entry.id,
    user_id: entry.userId,
    product_id: entry.productId,
    side: entry.side,
    snapshot: entry.snapshot,
    created_at: entry.createdAt.toISOString(),
    is_manual_save: entry.isManualSave,
  });

  if (error) {
    console.error('Failed to upload auto-save to Supabase:', error);
    throw new Error(`Supabase upload failed: ${error.message}`);
  }
}

/**
 * Download auto-save entries from Supabase for a specific user/product/side
 */
export async function downloadAutoSavesFromCloud(
  userId: string,
  productId: string,
  side: 'front' | 'back',
  isManualSave?: boolean
): Promise<AutoSaveEntry[]> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  let query = supabase
    .from('auto_save_history')
    .select('*')
    .eq('user_id', userId)
    .eq('product_id', productId)
    .eq('side', side)
    .order('created_at', { ascending: false });

  if (isManualSave !== undefined) {
    query = query.eq('is_manual_save', isManualSave);
  }

  query = query.limit(500);

  const { data, error } = await query;

  if (error) {
    console.error('Failed to download auto-saves from Supabase:', error);
    throw new Error(`Supabase download failed: ${error.message}`);
  }

  return (data || []).map((row) => ({
    id: row.id,
    userId: row.user_id,
    productId: row.product_id,
    side: row.side,
    snapshot: row.snapshot,
    createdAt: new Date(row.created_at),
    isManualSave: row.is_manual_save,
  }));
}

/**
 * Sync auto-saves bidirectionally between IndexedDB and Supabase
 * Strategy: Most recent timestamp wins
 */
export async function syncAutoSaves(
  userId: string,
  productId: string,
  side: 'front' | 'back'
): Promise<{
  uploaded: number;
  downloaded: number;
  conflicts: number;
}> {
  if (!isSupabaseConfigured()) {
    return { uploaded: 0, downloaded: 0, conflicts: 0 };
  }

  let uploaded = 0;
  let downloaded = 0;
  let conflicts = 0;

  try {
    // Get local auto-saves
    const localAutoSaves = await getAutoSaves(userId, productId, side, false);

    // Get cloud auto-saves
    const cloudAutoSaves = await downloadAutoSavesFromCloud(userId, productId, side, false);

    // Create maps for quick lookup
    const localMap = new Map(localAutoSaves.map((entry) => [entry.id, entry]));
    const cloudMap = new Map(cloudAutoSaves.map((entry) => [entry.id, entry]));

    // Find entries to upload (in local but not in cloud, or local is newer)
    for (const localEntry of localAutoSaves) {
      const cloudEntry = cloudMap.get(localEntry.id);

      if (!cloudEntry) {
        // Upload to cloud
        await uploadAutoSaveToCloud(localEntry);
        uploaded++;
      } else if (localEntry.createdAt > cloudEntry.createdAt) {
        // Local is newer - upload to cloud (conflict resolution)
        await uploadAutoSaveToCloud(localEntry);
        uploaded++;
        conflicts++;
      }
    }

    // Find entries to download (in cloud but not in local, or cloud is newer)
    for (const cloudEntry of cloudAutoSaves) {
      const localEntry = localMap.get(cloudEntry.id);

      if (!localEntry) {
        // Download to local
        await saveAutoSave(cloudEntry);
        downloaded++;
      } else if (cloudEntry.createdAt > localEntry.createdAt) {
        // Cloud is newer - download to local (conflict resolution)
        await saveAutoSave(cloudEntry);
        downloaded++;
        conflicts++;
      }
    }

    return { uploaded, downloaded, conflicts };
  } catch (error) {
    console.error('Sync failed:', error);
    throw error;
  }
}

/**
 * Get the latest auto-save from cloud (most recent)
 */
export async function getLatestAutoSaveFromCloud(
  userId: string,
  productId: string,
  side: 'front' | 'back'
): Promise<AutoSaveEntry | null> {
  if (!isSupabaseConfigured()) {
    return null;
  }

  try {
    const autoSaves = await downloadAutoSavesFromCloud(userId, productId, side, false);
    return autoSaves[0] ?? null;
  } catch (error) {
    console.error('Failed to get latest auto-save from cloud:', error);
    return null;
  }
}

/**
 * Delete auto-save from cloud
 */
export async function deleteAutoSaveFromCloud(id: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  const { error } = await supabase.from('auto_save_history').delete().eq('id', id);

  if (error) {
    console.error('Failed to delete auto-save from Supabase:', error);
    throw new Error(`Supabase delete failed: ${error.message}`);
  }
}

// ============================================
// Project & Design Cloud Sync
// ============================================

/**
 * Upload blob to Supabase Storage
 * @param blob - Blob to upload (PNG image)
 * @param userId - User ID for path scoping
 * @param category - 'projects' or 'designs'
 * @param itemId - Project or design ID
 * @param fileName - File name (e.g., 'thumbnail.png', 'front.png')
 * @returns Signed URL and storage path
 */
async function uploadBlobToStorage(
  blob: Blob,
  userId: string,
  category: 'projects' | 'designs',
  itemId: string,
  fileName: string
): Promise<{ url: string; path: string }> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  // Path: {userId}/{category}/{itemId}/{fileName}
  const path = `${userId}/${category}/${itemId}/${fileName}`;

  const { error } = await supabase.storage.from('designs').upload(path, blob, {
    cacheControl: '3600',
    upsert: true, // Allow overwrite on re-upload
  });

  if (error) {
    throw new Error(`Blob upload failed: ${error.message}`);
  }

  // Get signed URL (1 year expiry)
  const { data: urlData } = await supabase.storage
    .from('designs')
    .createSignedUrl(path, 60 * 60 * 24 * 365);

  if (!urlData?.signedUrl) {
    throw new Error('Failed to get signed URL');
  }

  return {
    url: urlData.signedUrl,
    path,
  };
}

/**
 * Download blob from Supabase Storage URL
 */
async function downloadBlobFromUrl(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Download failed: ${response.statusText}`);
  }
  return await response.blob();
}

/**
 * Upload project to Supabase cloud
 */
export async function uploadProjectToCloud(project: StoredProject, userId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  if (!project.productId) {
    throw new Error('Project missing productId - cannot sync');
  }

  let thumbnailUrl: string | null = null;

  // Upload thumbnail if exists
  if (project.thumbnailDataUrl) {
    try {
      const blob = await (await fetch(project.thumbnailDataUrl)).blob();
      const result = await uploadBlobToStorage(
        blob,
        userId,
        'projects',
        project.id,
        'thumbnail.png'
      );
      thumbnailUrl = result.url;
    } catch (error) {
      console.warn('[ProjectSync] Thumbnail upload failed:', error);
    }
  }

  // Upsert project to Supabase
  const { error } = await supabase.from('user_projects').upsert(
    {
      id: project.id,
      user_id: userId,
      name: project.name,
      product_id: project.productId,
      front_snapshot: project.snapshot,
      back_snapshot: null,
      thumbnail_url: thumbnailUrl,
      created_at: project.createdAt.toISOString(),
      updated_at: project.updatedAt.toISOString(),
    },
    {
      onConflict: 'id',
    }
  );

  if (error) {
    throw new Error(`Project upload failed: ${error.message}`);
  }
}

/**
 * Download projects from Supabase cloud
 */
export async function downloadProjectsFromCloud(
  userId: string,
  productId?: string
): Promise<StoredProject[]> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  let query = supabase
    .from('user_projects')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });

  if (productId) {
    query = query.eq('product_id', productId);
  }

  query = query.limit(500);

  const { data, error } = await query;

  if (error) {
    throw new Error(`Project download failed: ${error.message}`);
  }

  return (data || []).map((row) => ({
    id: row.id,
    userId: row.user_id,
    name: row.name,
    productId: row.product_id,
    snapshot: row.front_snapshot,
    thumbnailDataUrl: undefined, // Load on-demand
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  }));
}

/**
 * Sync projects bidirectionally between IndexedDB and Supabase
 * Strategy: Most recent timestamp wins
 */
export async function syncProjects(
  userId: string,
  productId?: string
): Promise<{ uploaded: number; downloaded: number; conflicts: number }> {
  if (!isSupabaseConfigured()) {
    return { uploaded: 0, downloaded: 0, conflicts: 0 };
  }

  let uploaded = 0;
  let downloaded = 0;
  let conflicts = 0;

  try {
    const localProjects = await getProjects();
    const cloudProjects = await downloadProjectsFromCloud(userId, productId);

    const localMap = new Map(localProjects.map((p) => [p.id, p]));
    const cloudMap = new Map(cloudProjects.map((p) => [p.id, p]));

    // Upload: local not in cloud OR local is newer
    for (const localProject of localProjects) {
      if (!localProject.productId) continue; // Skip projects without productId

      const cloudProject = cloudMap.get(localProject.id);

      if (!cloudProject) {
        await uploadProjectToCloud(localProject, userId);
        uploaded++;
      } else if (localProject.updatedAt > cloudProject.updatedAt) {
        await uploadProjectToCloud(localProject, userId);
        uploaded++;
        conflicts++;
      }
    }

    // Download: cloud not in local OR cloud is newer
    for (const cloudProject of cloudProjects) {
      const localProject = localMap.get(cloudProject.id);

      if (!localProject) {
        await saveProject(cloudProject);
        downloaded++;
      } else if (cloudProject.updatedAt > localProject.updatedAt) {
        await saveProject(cloudProject);
        downloaded++;
        conflicts++;
      }
    }

    return { uploaded, downloaded, conflicts };
  } catch (error) {
    console.error('[ProjectSync] Sync failed:', error);
    throw error;
  }
}

/**
 * Upload design to Supabase cloud
 */
export async function uploadDesignToCloud(design: StoredDesign, userId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  if (!design.frontBlob) {
    throw new Error('Design missing frontBlob');
  }

  // Upload front blob
  const frontResult = await uploadBlobToStorage(
    design.frontBlob,
    userId,
    'designs',
    design.id,
    'front.png'
  );

  // Upload back blob (optional)
  let backUrl: string | null = null;
  if (design.backBlob) {
    const backResult = await uploadBlobToStorage(
      design.backBlob,
      userId,
      'designs',
      design.id,
      'back.png'
    );
    backUrl = backResult.url;
  }

  // Upsert design to Supabase
  const { error } = await supabase.from('user_designs').upsert(
    {
      id: design.id,
      user_id: userId,
      product_id: design.metadata.productId,
      front_blob_url: frontResult.url,
      back_blob_url: backUrl,
      metadata: design.metadata,
      created_at: design.createdAt.toISOString(),
    },
    {
      onConflict: 'id',
    }
  );

  if (error) {
    throw new Error(`Design upload failed: ${error.message}`);
  }
}

/**
 * Download designs from Supabase cloud
 */
export async function downloadDesignsFromCloud(
  userId: string,
  productId?: string
): Promise<StoredDesign[]> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  let query = supabase
    .from('user_designs')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (productId) {
    query = query.eq('product_id', productId);
  }

  query = query.limit(500);

  const { data, error } = await query;

  if (error) {
    throw new Error(`Design download failed: ${error.message}`);
  }

  // Download blobs
  const designs = await Promise.all(
    (data || []).map(async (row) => {
      const frontBlob = await downloadBlobFromUrl(row.front_blob_url);
      const backBlob = row.back_blob_url ? await downloadBlobFromUrl(row.back_blob_url) : null;

      return {
        id: row.id,
        userId: row.user_id,
        frontBlob,
        backBlob,
        createdAt: new Date(row.created_at),
        metadata: row.metadata,
      };
    })
  );

  return designs;
}

/**
 * Sync designs bidirectionally between IndexedDB and Supabase
 * Strategy: Cloud designs not in local are downloaded
 */
export async function syncDesigns(
  userId: string,
  productId?: string
): Promise<{ uploaded: number; downloaded: number }> {
  if (!isSupabaseConfigured()) {
    return { uploaded: 0, downloaded: 0 };
  }

  let uploaded = 0;
  let downloaded = 0;

  try {
    const localDesigns = await getDesigns();

    // Get cloud IDs only (for performance)
    let query = supabase.from('user_designs').select('id').eq('user_id', userId);
    if (productId) {
      query = query.eq('product_id', productId);
    }

    const { data: cloudMeta, error } = await query;
    if (error) throw error;

    const cloudIds = new Set(cloudMeta?.map((d) => d.id) || []);
    const localMap = new Map(localDesigns.map((d) => [d.id, d]));

    // Upload: local designs not in cloud
    for (const localDesign of localDesigns) {
      if (!localDesign.userId) continue; // Skip designs without userId

      if (!cloudIds.has(localDesign.id)) {
        await uploadDesignToCloud(localDesign, userId);
        uploaded++;
      }
    }

    // Download: cloud designs not in local
    const missingIds = Array.from(cloudIds).filter((id) => !localMap.has(id));

    if (missingIds.length > 0) {
      const designs = await downloadDesignsFromCloud(userId, productId);
      for (const design of designs) {
        if (missingIds.includes(design.id)) {
          await saveDesign(design);
          downloaded++;
        }
      }
    }

    return { uploaded, downloaded };
  } catch (error) {
    console.error('[DesignSync] Sync failed:', error);
    throw error;
  }
}

// ============================================
// Cart Cloud Sync
// ============================================

/**
 * Upload cart item to Supabase cloud
 */
export async function uploadCartItemToCloud(item: CartItem, userId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  console.warn('[CartSync] Uploading cart item:', item.id, 'for user:', userId);

  // Upload front design blob
  let frontUrl: string | null = null;
  if (item.frontDesignBlob) {
    console.warn('[CartSync] Uploading front blob, size:', item.frontDesignBlob.size);
    const result = await uploadBlobToStorage(
      item.frontDesignBlob,
      userId,
      'cart' as 'projects', // Reuse category type
      item.id,
      'front.png'
    );
    frontUrl = result.url;
    console.warn('[CartSync] Front blob uploaded:', frontUrl ? 'success' : 'failed');
  } else {
    console.warn('[CartSync] No front blob to upload');
  }

  // Upload back design blob (optional)
  let backUrl: string | null = null;
  if (item.backDesignBlob) {
    const result = await uploadBlobToStorage(
      item.backDesignBlob,
      userId,
      'cart' as 'projects',
      item.id,
      'back.png'
    );
    backUrl = result.url;
  }

  // Upload production-ready front (optional)
  let frontProdUrl: string | null = null;
  if (item.frontProductionDataUrl) {
    try {
      const blob = await (await fetch(item.frontProductionDataUrl)).blob();
      const result = await uploadBlobToStorage(
        blob,
        userId,
        'cart' as 'projects',
        item.id,
        'front-prod.png'
      );
      frontProdUrl = result.url;
    } catch (e) {
      console.warn('[CartSync] Production front upload failed:', e);
    }
  }

  // Upload production-ready back (optional)
  let backProdUrl: string | null = null;
  if (item.backProductionDataUrl) {
    try {
      const blob = await (await fetch(item.backProductionDataUrl)).blob();
      const result = await uploadBlobToStorage(
        blob,
        userId,
        'cart' as 'projects',
        item.id,
        'back-prod.png'
      );
      backProdUrl = result.url;
    } catch (e) {
      console.warn('[CartSync] Production back upload failed:', e);
    }
  }

  // Upsert to Supabase
  console.warn('[CartSync] Upserting to user_cart table...');
  const { error } = await supabase.from('user_cart').upsert(
    {
      id: item.id,
      user_id: userId,
      product_id: item.productId,
      product_name: item.productName,
      front_design_url: frontUrl,
      back_design_url: backUrl,
      front_production_url: frontProdUrl,
      back_production_url: backProdUrl,
      thumbnail_url: item.designThumbnail || null,
      quantity: item.quantity,
      price_cents: item.priceInCents,
      added_at: item.addedAt.toISOString(),
      updated_at: (item.updatedAt || new Date()).toISOString(),
      metadata: item.metadata,
    },
    {
      onConflict: 'id',
    }
  );

  if (error) {
    console.error('[CartSync] Database upsert failed:', error);
    throw new Error(`Cart item upload failed: ${error.message}`);
  }

  console.warn('[CartSync] Cart item uploaded successfully:', item.id);
}

/**
 * Download cart items from Supabase cloud
 */
export async function downloadCartFromCloud(userId: string): Promise<CartItem[]> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  console.warn('[CartSync] Downloading cart from cloud for user:', userId);

  const { data, error } = await supabase
    .from('user_cart')
    .select('*')
    .eq('user_id', userId)
    .order('added_at', { ascending: false })
    .limit(500);

  if (error) {
    console.error('[CartSync] Download query failed:', error);
    throw new Error(`Cart download failed: ${error.message}`);
  }

  console.warn('[CartSync] Found', data?.length || 0, 'items in cloud cart');

  // Download blobs for each cart item
  const cartItems = await Promise.all(
    (data || []).map(async (row) => {
      let frontBlob: Blob | null = null;
      let backBlob: Blob | null = null;

      // Download front design
      if (row.front_design_url) {
        try {
          frontBlob = await downloadBlobFromUrl(row.front_design_url);
        } catch (e) {
          console.warn('[CartSync] Front blob download failed:', e);
        }
      }

      // Download back design
      if (row.back_design_url) {
        try {
          backBlob = await downloadBlobFromUrl(row.back_design_url);
        } catch (e) {
          console.warn('[CartSync] Back blob download failed:', e);
        }
      }

      // If no front blob, skip this item (corrupt data)
      if (!frontBlob) {
        return null;
      }

      const item: CartItem = {
        id: row.id,
        userId: row.user_id,
        productId: row.product_id,
        productName: row.product_name,
        frontDesignBlob: frontBlob,
        backDesignBlob: backBlob,
        frontProductionDataUrl: row.front_production_url || null,
        backProductionDataUrl: row.back_production_url || null,
        designThumbnail: row.thumbnail_url || undefined,
        quantity: row.quantity,
        priceInCents: row.price_cents,
        addedAt: new Date(row.added_at),
        updatedAt: new Date(row.updated_at),
        metadata: row.metadata,
      };

      return item;
    })
  );

  // Filter out null items (failed downloads)
  return cartItems.filter((item): item is CartItem => item !== null);
}

/**
 * Delete cart item from cloud
 */
export async function deleteCartItemFromCloud(itemId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  const { error } = await supabase.from('user_cart').delete().eq('id', itemId);

  if (error) {
    throw new Error(`Cart item delete failed: ${error.message}`);
  }
}

/**
 * Clear all cart items for user from cloud
 */
export async function clearCartFromCloud(userId: string): Promise<number> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }

  const { error, count } = await supabase
    .from('user_cart')
    .delete({ count: 'exact' })
    .eq('user_id', userId);

  if (error) {
    throw new Error(`Cart clear failed: ${error.message}`);
  }

  return count || 0;
}

/**
 * Sync cart bidirectionally between IndexedDB and Supabase
 * Strategy: Most recent updatedAt wins for conflicts
 */
export async function syncCart(
  userId: string
): Promise<{ uploaded: number; downloaded: number; conflicts: number }> {
  if (!isSupabaseConfigured()) {
    return { uploaded: 0, downloaded: 0, conflicts: 0 };
  }

  let uploaded = 0;
  let downloaded = 0;
  let conflicts = 0;

  try {
    const localItems = await getCartItems();
    const cloudItems = await downloadCartFromCloud(userId);

    const localMap = new Map(localItems.map((item) => [item.id, item]));
    const cloudMap = new Map(cloudItems.map((item) => [item.id, item]));

    // Upload: local items not in cloud OR local is newer
    for (const localItem of localItems) {
      // Skip items without userId (not owned by this user)
      if (localItem.userId && localItem.userId !== userId) continue;

      const cloudItem = cloudMap.get(localItem.id);

      if (!cloudItem) {
        await uploadCartItemToCloud(localItem, userId);
        uploaded++;
      } else {
        const localTime = localItem.updatedAt?.getTime() || localItem.addedAt.getTime();
        const cloudTime = cloudItem.updatedAt?.getTime() || cloudItem.addedAt.getTime();

        if (localTime > cloudTime) {
          await uploadCartItemToCloud(localItem, userId);
          uploaded++;
          conflicts++;
        }
      }
    }

    // Download: cloud items not in local OR cloud is newer
    for (const cloudItem of cloudItems) {
      const localItem = localMap.get(cloudItem.id);

      if (!localItem) {
        await saveCartItem(cloudItem);
        downloaded++;
      } else {
        const localTime = localItem.updatedAt?.getTime() || localItem.addedAt.getTime();
        const cloudTime = cloudItem.updatedAt?.getTime() || cloudItem.addedAt.getTime();

        if (cloudTime > localTime) {
          await saveCartItem(cloudItem);
          downloaded++;
          conflicts++;
        }
      }
    }

    // Handle deletions: items in local but removed from cloud (by another device)
    const cloudIds = new Set(cloudItems.map((item) => item.id));
    for (const localItem of localItems) {
      // Only check items that were synced (have userId)
      if (localItem.userId === userId && !cloudIds.has(localItem.id)) {
        // Check if this is a new local item that hasn't been uploaded yet
        // We can't distinguish, so we'll leave it and let it upload next sync
        // This preserves offline-first behavior
      }
    }

    return { uploaded, downloaded, conflicts };
  } catch (error) {
    console.error('[CartSync] Sync failed:', error);
    throw error;
  }
}
